import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
} from '../../../components/ui/Dialog';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import { Skeleton } from '../../../components/ui/Skeleton';
import { DialogErrorAlert, useDialogError } from '../../../components/ui/DialogError';
import { useReportExport } from '../../../components/reports/useReportExport';
import { reportExportFilename, reportExportLabels } from '../../../components/reports/reportExportRequest';
import { exportEmployeeDirectory, getDirectoryExportColumns } from '../../../api/General/account';
import type { DirectoryExportColumnView, DirectoryExportTier, EmployeeDirectoryExportInput } from '../../../models/general';

/*
  CO-920 — employee directory export (owner decisions 2026-10-21).

  A dialog, against the "modal as first thought" ban, because the client asked for a popup and a scope
  choice plus 17-44 columns does not fit in the toolbar. What the dialog may NOT do is decide anything
  that matters: the column list is exactly what `GET api/Account/Users/Export/Columns` returned for this
  caller (identity documents HR only, personal details Hr+Admin, Manager/Chief basic+contact), and the
  server re-reads every row and value. "Shown" sends the ids on screen, which the server intersects with
  the caller's own scope, so the request can only narrow the file, never widen it.
*/

export type DirectoryExportSegment = EmployeeDirectoryExportInput['segment'];
type Mode = EmployeeDirectoryExportInput['mode'];

const TIER_ORDER: readonly DirectoryExportTier[] = ['basic', 'contact', 'personal', 'identityDocuments'];

const LOOKUP_VALUES: Record<string, readonly string[]> = {
  role: ['Clocking', 'Manager', 'Chief', 'Hr', 'Admin', 'Operator'],
  employmentStatus: ['Active', 'OnProbation', 'Onboarding', 'Offboarding', 'Terminated', 'Resigned'],
  employmentType: ['All', 'FullTime', 'PartTime', 'Contract', 'Executive'],
  gender: ['Female', 'Male'],
  maritalStatus: ['Single', 'Engaged', 'Married', 'Divorced', 'Widowed'],
  visaCategory: ['SeniorExecutive', 'Professional', 'Skilled', 'SemiSkilled', 'Domestic'],
};

// Checkbox.jsx renders the real <input> sr-only and the visible box as a label that never takes focus,
// so the row draws the keyboard focus ring itself (WCAG 2.4.7).
const ROW_FOCUS = '-mx-1 flex items-center gap-2 rounded px-1 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-primary/30';

const storageKey = (userId?: string) => `co920.directoryExport.columns.${userId ?? 'anon'}`;

function readRemembered(userId?: string): string[] | null {
  try {
    const raw = window.localStorage.getItem(storageKey(userId));
    const parsed = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) ? parsed.filter((k): k is string => typeof k === 'string') : null;
  } catch {
    return null;
  }
}

function remember(userId: string | undefined, keys: string[]) {
  try {
    window.localStorage.setItem(storageKey(userId), JSON.stringify(keys));
  } catch {
    // Convenience only: a blocked storage just means the defaults come back next time.
  }
}

type Props = Readonly<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Ids of the rows the page is showing (filtered + sorted), in screen order. */
  shownIds: string[];
  /** Every row the caller can see on this segment (the page's `users.length`). */
  totalCount: number;
  segment: DirectoryExportSegment;
  /** True when a filter, a search or a non-Current segment narrows the list. */
  narrowed: boolean;
  /** Localized summary of the active filters, printed in the sheet's scope line. */
  filterSummary: string;
  userId?: string;
  rtl?: boolean;
  /**
   * The page's Export button. The dialog is controlled with no DialogTrigger, so Radix's own close
   * handler focuses a null trigger and, having called preventDefault, leaves focus on <body>.
   */
  returnFocusRef?: React.RefObject<HTMLElement>;
}>;

const ExportEmployeesDialog: React.FC<Props> = ({
  open, onOpenChange, shownIds, totalCount, segment, narrowed, filterSummary, userId, rtl, returnFocusRef,
}) => {
  const { t, i18n } = useTranslation();
  const { busy, exportReport } = useReportExport();
  const loadError = useDialogError();
  const [columns, setColumns] = useState<DirectoryExportColumnView[] | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [mode, setMode] = useState<Mode>('All');
  const isBusy = busy !== null;
  const archived = segment === 'archived';

  const labelFor = useCallback(
    (key: string) => t(`employees:export.column.${key}`, { defaultValue: key }),
    [t],
  );

  const load = useCallback(async () => {
    loadError.clear();
    setColumns(null);
    try {
      const result = await getDirectoryExportColumns();
      if (result.errors?.length) {
        loadError.showMessages([result.errors[0]?.description ?? t('employees:export.loadFailed')], 'server');
        return;
      }
      const list = result.payload ?? [];
      const allowed = new Set(list.map((c) => c.key));
      const remembered = readRemembered(userId)?.filter((k) => allowed.has(k));
      setColumns(list);
      setSelected(new Set(remembered && remembered.length > 0
        ? remembered
        : list.filter((c) => c.defaultSelected).map((c) => c.key)));
    } catch (err) {
      loadError.capture(err, t('employees:export.loadFailed'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, userId]);

  useEffect(() => {
    if (!open) return;
    // An empty "shown" set (a search that matches nobody) would preselect a scope that cannot download.
    setMode(narrowed && shownIds.length > 0 ? 'Shown' : 'All');
    void load();
    // `shownIds` is read once on open; re-running on every new array would reset the user's choice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, narrowed, load]);

  const groups = useMemo(() => {
    if (!columns) return [];
    return TIER_ORDER
      .map((tier) => ({ tier, items: columns.filter((c) => c.tier === tier) }))
      .filter((g) => g.items.length > 0);
  }, [columns]);

  const toggle = (keys: string[], on: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      keys.forEach((k) => (on ? next.add(k) : next.delete(k)));
      return next;
    });
  };

  const orderedSelection = (columns ?? []).filter((c) => selected.has(c.key)).map((c) => c.key);
  const selectedCount = orderedSelection.length;
  const shownCount = shownIds.length;
  const rowCount = mode === 'Shown' ? shownCount : totalCount;

  const download = async () => {
    if (selectedCount === 0 || rowCount === 0) return;
    remember(userId, orderedSelection);
    const title = t('employees:export.title');
    const today = new Date();
    const lifecycleLookup = {
      Active: t('employees:lifecycle.current'),
      Leaving: t('employees:lifecycle.leaving'),
      Left: t('employees:lifecycle.former'),
      Archived: t('employees:lifecycle.archived'),
    };
    const lookups: Record<string, Record<string, string>> = { lifecycleState: lifecycleLookup };
    Object.entries(LOOKUP_VALUES).forEach(([key, values]) => {
      lookups[key] = Object.fromEntries(values.map((v) => [v, t(`employees:export.value.${key}.${v}`)]));
    });
    const scopeText = mode === 'Shown'
      ? (filterSummary || t('employees:export.scopeShownSummary'))
      : (archived ? t('employees:export.scopeAllArchivedSummary') : t('employees:export.scopeAllSummary'));
    const input: EmployeeDirectoryExportInput = {
      mode,
      segment,
      userIds: mode === 'Shown' ? shownIds : undefined,
      scopeText,
    };
    let delivered = false;
    await exportReport('xlsx', async () => {
      const file = await exportEmployeeDirectory({
        input,
        format: 'xlsx',
        culture: i18n.language,
        rtl: !!rtl,
        title,
        // The server replaces every kind with its own; 'text' is only a placeholder.
        columns: orderedSelection.map((key) => ({ key, header: labelFor(key), kind: 'text' })),
        labels: {
          ...reportExportLabels(t),
          period: new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(today),
          scope: scopeText,
        },
        lookups,
      });
      delivered = true;
      return file;
    }, reportExportFilename(title, today.toISOString().slice(0, 10), 'xlsx'));
    if (delivered) onOpenChange(false);
  };

  const scopeOptions: Array<{ value: Mode; label: string; count: number }> = [
    {
      value: 'Shown',
      label: archived ? t('employees:export.scopeShownArchived') : t('employees:export.scopeShown'),
      count: shownCount,
    },
    {
      value: 'All',
      label: archived ? t('employees:export.scopeAllArchived') : t('employees:export.scopeAll'),
      count: totalCount,
    },
  ];

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // In flight, every dismissal path is locked together (ui-standards: dialogs).
        if (!next && isBusy) return;
        onOpenChange(next);
      }}
    >
      <DialogContent
        size="lg"
        // A flex column so the footer never scrolls away at 390x844: the column list shrinks first
        // (down to its floor), and only then does the body itself scroll.
        className="flex flex-col overflow-hidden"
        data-testid="directory-export-dialog"
        hideCloseButton={isBusy}
        onEscapeKeyDown={(e) => { if (isBusy) e.preventDefault(); }}
        onPointerDownOutside={(e) => { if (isBusy) e.preventDefault(); }}
        onCloseAutoFocus={(e) => {
          // Composed before Radix's no-op trigger focus; without it focus falls to <body> on close.
          if (!returnFocusRef?.current) return;
          e.preventDefault();
          returnFocusRef.current.focus();
        }}
      >
        <DialogHeader className="shrink-0">
          <DialogTitle>{t('employees:export.dialogTitle')}</DialogTitle>
          <DialogDescription>{t('employees:export.description')}</DialogDescription>
        </DialogHeader>
        <DialogBody className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto">
          <section className="flex flex-col gap-2">
            <h3 id="co920-scope-label" className="text-sm font-medium text-foreground">{t('employees:export.scopeLabel')}</h3>
            <div
              role="radiogroup"
              aria-labelledby="co920-scope-label"
              data-testid="directory-export-scope"
              className="inline-flex flex-wrap self-start rounded-md border border-border bg-card p-0.5"
            >
              {scopeOptions.map((option) => {
                const active = mode === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    // Nothing on screen means nothing to export under "Shown"; the count says why.
                    disabled={isBusy || option.count === 0}
                    onClick={() => setMode(option.value)}
                    data-testid={`directory-export-scope-${option.value.toLowerCase()}`}
                    className={`rounded px-3 py-1.5 text-sm font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 disabled:cursor-not-allowed disabled:opacity-50 ${active ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground'}`}
                  >
                    {option.label}
                    <span className="ms-1.5 tabular-nums opacity-70">{option.count}</span>
                  </button>
                );
              })}
            </div>
          </section>

          <section className="flex min-h-0 flex-1 flex-col gap-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-sm font-medium text-foreground">
                {t('employees:export.columnsLabel')}
                {columns && (
                  <span className="ms-2 font-normal text-muted-foreground tabular-nums" data-testid="directory-export-selected-count">
                    {t('employees:export.columnsSelected', { selected: selectedCount, total: columns.length })}
                  </span>
                )}
              </h3>
              {columns && columns.length > 0 && (
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="sm" disabled={isBusy} onClick={() => toggle(columns.map((c) => c.key), true)}>
                    {t('employees:export.selectAll')}
                  </Button>
                  <Button variant="ghost" size="sm" disabled={isBusy} onClick={() => setSelected(new Set())}>
                    {t('employees:export.clear')}
                  </Button>
                </div>
              )}
            </div>

            <DialogErrorAlert error={loadError.error} />
            {loadError.error && (
              <Button variant="outline" size="sm" iconName="RefreshCw" className="self-start" onClick={() => void load()}>
                {t('employees:export.retry')}
              </Button>
            )}

            {!columns && !loadError.error && (
              <div className="flex flex-col gap-2" aria-busy="true" aria-label={t('employees:export.loading')}>
                <Skeleton className="h-4 w-1/3" />
                <Skeleton className="h-4 w-2/3" />
                <Skeleton className="h-4 w-1/2" />
              </div>
            )}

            {columns && columns.length === 0 && (
              <p className="text-sm text-muted-foreground">{t('employees:export.noColumns')}</p>
            )}

            {groups.length > 0 && (
              // `relative` is load-bearing: the Checkbox inputs are sr-only (position:absolute), and this
              // bounded scroller must be their containing block or they escape it (ui-standards: Scrolling).
              <div className="relative min-h-32 max-h-[50vh] flex-1 overflow-y-auto rounded-md border border-border" data-testid="directory-export-columns">
                {groups.map((group, index) => {
                  const keys = group.items.map((c) => c.key);
                  const onCount = keys.filter((k) => selected.has(k)).length;
                  const all = onCount === keys.length;
                  const some = onCount > 0 && !all;
                  const groupId = `co920-group-${group.tier}`;
                  return (
                    <fieldset
                      key={group.tier}
                      data-testid={`directory-export-group-${group.tier}`}
                      className={`px-4 py-3 ${index > 0 ? 'border-t border-border' : ''}`}
                    >
                      <legend className="sr-only">{t(`employees:export.group.${group.tier}`)}</legend>
                      <div className={ROW_FOCUS}>
                        <Checkbox
                          id={groupId}
                          checked={all}
                          indeterminate={some}
                          aria-checked={some ? 'mixed' : all}
                          disabled={isBusy}
                          onChange={() => toggle(keys, !all)}
                        />
                        <label htmlFor={groupId} className="cursor-pointer text-sm font-semibold text-foreground">
                          {t(`employees:export.group.${group.tier}`)}
                        </label>
                        <span className="text-xs tabular-nums text-muted-foreground">{onCount}/{keys.length}</span>
                      </div>
                      <div className="mt-3 grid grid-cols-1 gap-x-6 gap-y-2 ps-6 sm:grid-cols-2">
                        {group.items.map((column) => {
                          const id = `co920-col-${column.key}`;
                          return (
                            <div key={column.key} className={ROW_FOCUS}>
                              <Checkbox
                                id={id}
                                checked={selected.has(column.key)}
                                disabled={isBusy}
                                data-column-key={column.key}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => toggle([column.key], e.target.checked)}
                              />
                              <label htmlFor={id} className="cursor-pointer text-sm text-foreground">{labelFor(column.key)}</label>
                            </div>
                          );
                        })}
                      </div>
                    </fieldset>
                  );
                })}
              </div>
            )}
          </section>
        </DialogBody>
        <DialogFooter className="shrink-0 flex-wrap">
          <Button variant="outline" disabled={isBusy} onClick={() => onOpenChange(false)}>{t('Cancel')}</Button>
          <Button
            variant="default"
            iconName="Download"
            loading={isBusy}
            disabled={!columns || selectedCount === 0 || rowCount === 0}
            onClick={() => void download()}
            data-testid="directory-export-download"
          >
            {t('employees:export.download', { n: selectedCount })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ExportEmployeesDialog;
